import React, { useId, useRef, useEffect, useState } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { Typeahead } from 'react-bootstrap-typeahead';
import {
    FormGroup,
} from 'react-bootstrap';
import FormLabel from "./form/FormLabel";
import './FormDropdown.scss';
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {APP_COLORS} from "../Constants";
import {faLock} from "@fortawesome/pro-solid-svg-icons";
import {faTimes} from "@fortawesome/free-solid-svg-icons";

const DivLockRelative = styled.div `
  position: relative;
`;

const DivLockAbsolute = styled.div `
  position: absolute;
  
  right: 0;
  top: -24px;
`;

const ClearRelative = styled.div `
  position: relative;
  display: inline-block;
  width: 100%;
`;

const ClearAbsolute = styled.div `
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
  pointer-events: auto;
  opacity: 0.5;
  transition: opacity 0.15s ease-in-out, color 0.15s ease-in-out;
  
  svg,
  svg.svg-inline--fa,
  .svg-inline--fa,
  svg[class*="svg-inline"] {
    font-size: 20px !important;
    width: 20px !important;
    height: 20px !important;
    min-width: 20px !important;
    min-height: 20px !important;
    max-width: 20px !important;
    max-height: 20px !important;
  }
  
  &:hover {
    opacity: 1;
    color: ${APP_COLORS['red']} !important;
    
    svg,
    svg.svg-inline--fa,
    .svg-inline--fa,
    svg[class*="svg-inline"] {
      font-size: 20px !important;
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

function ComboBox(props) {
    const {
        id,
        style,
        size,
        className,
        label,
        placeholder,
        disabled,
        onChange,
        options,
        labelKey,
        multiple,
        selected,
        clearButton,
        isLoading,
        isPreview = false,
        infoText = '',
        renderMenuItemChildren,
        allowNew = false,
        keepOpen = false,
    } = props;

    const autoId = useId();
    const typeaheadRef = useRef(null);
    const inputTextRef = useRef('');
    const [isOpen, setIsOpen] = useState(false);

    // Ensure labelKey is always a function
    const safeLabelKey = typeof labelKey === 'function' ? labelKey : (option) => option.display;

    // react-bootstrap-typeahead requires labelKey to be a plain string (not a
    // function) when allowNew is set — it uses it directly as the property
    // name to build the new-option object ({customOption: true, [labelKey]: text}).
    const typeaheadLabelKey = allowNew ? 'display' : safeLabelKey;

    // Debug logging for controlled/uncontrolled diagnosis
    try {
        // Only log when something looks suspicious to reduce noise
        if (selected !== undefined && !Array.isArray(selected)) {
            // selected present but not an array
            // eslint-disable-next-line no-console
            console.warn('[ComboBox] selected is not an array', { id, selected });
        }
        if (options !== undefined && !Array.isArray(options)) {
            // options present but not an array
            // eslint-disable-next-line no-console
            console.warn('[ComboBox] options is not an array', { id, options });
        }
        // Always provide a compact heartbeat per instance in dev
        // eslint-disable-next-line no-console
        // console.log('[ComboBox] mount', {
        //     id,
        //     selectedIsArray: Array.isArray(selected),
        //     selectedLen: Array.isArray(selected) ? selected.length : undefined,
        //     optionsIsArray: Array.isArray(options),
        //     optionsLen: Array.isArray(options) ? options.length : undefined,
        //     clearButton,
        // });
    } catch (_) {}

    // Sanitize options to ensure they have proper structure for typeahead
    const sanitizedOptions = (options || [])
        .filter(option => option && typeof option === 'object' && (option.id || option.display))
        .map(option => {
            const sanitizedOption = { ...option }; // Preserve original structure
            
            // Ensure id and display are strings for typeahead compatibility
            sanitizedOption.id = String(option.id || option.display || 'unknown');
            sanitizedOption.display = String(option.display || option.id || 'Unnamed');
            
            // Ensure display is never empty
            if (!sanitizedOption.display || sanitizedOption.display === 'undefined' || sanitizedOption.display === 'null') {
                sanitizedOption.display = 'Unnamed';
            }
            
            return sanitizedOption;
        });

    // Sanitize selected values (preserve original structure)
    const selectedArray = Array.isArray(selected) ? selected : (selected ? [selected] : []);
    const sanitizedSelected = selectedArray
        .filter(item => item && typeof item === 'object')
        .map(item => {
            const sanitizedItem = { ...item }; // Preserve original structure
            
            // Ensure id and display are strings for typeahead compatibility
            sanitizedItem.id = String(item.id || '');
            sanitizedItem.display = String(item.display || 'Unnamed');
            
            return sanitizedItem;
        });

    // Ensure we never pass empty or invalid options — except for allowNew
    // fields (e.g. free-text postal codes), which have no predefined option
    // list at all; an empty array there is the correct steady state, not a
    // loading artifact, so the "Loading..." placeholder must not apply.
    const safeOptions = allowNew || sanitizedOptions.length > 0 ? sanitizedOptions : [
        { id: 'loading', display: 'Loading...' }
    ];
    
    // Always provide an array for selected
    const safeSelected = sanitizedSelected || [];

    // Handle clear button click
    const handleClear = () => {
        onChange([]);
        // Also clear via Typeahead ref if available
        if (typeaheadRef.current && typeaheadRef.current.clear) {
            typeaheadRef.current.clear();
        }
    };

    // For allowNew fields, plain Enter does nothing unless a menu item is
    // highlighted first (react-bootstrap-typeahead's single-result auto-highlight
    // is explicitly disabled whenever allowNew is set, so there's never an
    // active item to submit) — normally that forces a mouse click, or
    // ArrowDown+Enter. Call the same internal method the library uses for a
    // real click (_handleSelectionAdd) so Enter alone adds the typed value,
    // via the exact same code path a click would use.
    const handleAllowNewKeyDown = (e) => {
        if (!allowNew || e.key !== 'Enter') return;
        const text = inputTextRef.current.trim();
        if (!text) return;
        const instance = typeaheadRef.current;
        if (instance && typeof instance._handleSelectionAdd === 'function') {
            instance._handleSelectionAdd({customOption: true, display: text});
        }
    };

    // keepOpen fields (e.g. long multi-select lists like Brand Affinities)
    // stay open across selections instead of the library's default of
    // closing the menu after every pick — selecting an option doesn't blur
    // the input (the library prevents that), so the menu would otherwise
    // need reopening for each checkbox. `open` is a controlled prop that
    // takes precedence over the library's internal auto-close entirely, so
    // it's left undefined (uncontrolled, default behavior) for every other
    // field and only driven by our own isOpen state here.
    const handleFocus = () => {
        if (keepOpen) setIsOpen(true);
    };
    const handleBlur = () => {
        if (keepOpen) setIsOpen(false);
    };
    const handleKeyDown = (e) => {
        handleAllowNewKeyDown(e);
        if (keepOpen && e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <FormGroup
            id={id}
            className={`${className} form-text-input-group`}
            style={style}
        >
            <FormLabel className={"formTextLabel"} isPreview={isPreview} infoText={infoText}>
                {label}
            </FormLabel>
            <ClearRelative>
                <Typeahead
                    ref={typeaheadRef}
                    key={`combo-box-${id}-${autoId}`}
                    id={`combo-box-${id}-${autoId}`}
                    clearButton={false}
                    labelKey={typeaheadLabelKey}
                    size={size}
                    options={safeOptions}
                    placeholder={placeholder}
                    disabled={disabled}
                    multiple={multiple}
                    allowNew={allowNew}
                    open={keepOpen ? isOpen : undefined}
                    ignoreDiacritics
                    selected={safeSelected}
                    positionFixed={true}
                    flip={true}
                    isLoading={isLoading}
                    renderMenuItemChildren={renderMenuItemChildren}
                    onInputChange={(text) => { inputTextRef.current = text; }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    onChange={(model) => {
                        // The library assigns its own meaningless id
                        // (uniqueId('new-id-...')) to a newly-typed (allowNew)
                        // entry — override it with the typed text itself so
                        // downstream code (which keys off option.id, e.g. to
                        // build the actual Cube.js filter value) uses the real
                        // value rather than a random placeholder id.
                        const normalized = model.map((item) => (
                            item?.customOption
                                ? {...item, id: String(item.display)}
                                : item
                        ));
                        return onChange(normalized);
                    }}
                />
                {clearButton && safeSelected.length > 0 && !disabled && (
                    <ClearAbsolute onClick={handleClear}>
                        <FontAwesomeIcon
                            color={APP_COLORS['gray-500']}
                            icon={faTimes}
                        />
                    </ClearAbsolute>
                )}
            </ClearRelative>
            {props.disabled && props.lockIcon && (
                <DivLockRelative>
                    <DivLockAbsolute>
                        <FontAwesomeIcon
                            color={APP_COLORS['gray-600']}
                            icon={faLock}
                            style={{marginRight: '10px', fontSize: 15}}
                        />
                    </DivLockAbsolute>
                </DivLockRelative>
            )}
        </FormGroup>
    )
}

ComboBox.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    toggleStyle: PropTypes.object,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            display: PropTypes.string.isRequired,
        })
    ),
    onChange: PropTypes.func.isRequired,
    labelKey: PropTypes.func,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    selected: PropTypes.arrayOf(
    PropTypes.shape({
            id: PropTypes.string.isRequired,
            display: PropTypes.string.isRequired,
        })
    ),
    clearButton: PropTypes.bool,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'small', 'md', 'lg', 'large']),
    lockIcon: PropTypes.bool,
    renderMenuItemChildren: PropTypes.func,
    allowNew: PropTypes.bool,
    keepOpen: PropTypes.bool,
};

ComboBox.defaultProps = {
    style: {},
    className: '',
    labelStyle: {},
    validation: {},
    variant: 'link',
    disabled: false,
    options: [],
    value: {},
    size: 'sm',
    toggleStyle: {textDecoration: 'none'},
    onChange: e => e,
    labelKey: (option) => option.display,
    allowNew: false,
    keepOpen: false,
    multiple: false,
    clearButton: false,
    label: '',
    lockIcon: false,
};
export default ComboBox;
